import { useMemo } from 'react';
import PageHero from '../components/PageHero.jsx';
import DeadlineBanner from '../components/DeadlineBanner.jsx';
import InfoIcon from '../components/InfoIcon.jsx';
import Pill from '../components/Pill.jsx';
import { TIMELINE } from '../data/timeline.js';
import { formatDate, daysUntil } from '../lib/dates.js';
import './Timeline.css';

const CATEGORY_LABELS = {
  milestone:      'Milestone',
  deadline:       'Deadline',
  registration:   'Registration',
  'school-event': 'School Event',
  academic:       'Academic',
};

const CATEGORY_VARIANT = {
  milestone:      'primary',
  deadline:       'danger',
  registration:   'accent',
  'school-event': 'success',
  academic:       'default',
};

function entryStatus(isoDate) {
  const days = daysUntil(isoDate);
  if (days < 0)  return 'past';
  if (days === 0) return 'today';
  return 'future';
}

export default function Timeline() {
  const { dated, tba, bannerEntries } = useMemo(() => {
    const dated = TIMELINE
      .filter(e => e.date)
      .sort((a, b) => new Date(a.date + 'T12:00:00') - new Date(b.date + 'T12:00:00'));

    const tba = TIMELINE.filter(e => !e.date);

    const bannerEntries = TIMELINE.filter(
      e => e.date && (e.priority === 'high' || e.actionRequired)
    );

    return { dated, tba, bannerEntries };
  }, []);

  return (
    <div className="timeline-page">
      <PageHero icon="/assets/eagle/page-icons/timeline.png" title="Year at a Glance" />

      <p className="timeline-subtitle">
        Key dates and deadlines for the 2026–27 school year, in order. Action-required
        entries are highlighted so nothing slips through the cracks.
      </p>

      <DeadlineBanner entries={bannerEntries} max={2} />

      {/* ── Dated entries ── */}
      <section aria-label="Dated events and deadlines">
        <div className="timeline-track" role="list">
          {dated.map(entry => {
            const status = entryStatus(entry.date);
            const isPast = status === 'past';
            const isToday = status === 'today';

            return (
              <div
                key={entry.id}
                className={[
                  'timeline-entry',
                  `timeline-entry--${status}`,
                  `timeline-entry--priority-${entry.priority}`,
                  entry.actionRequired ? 'timeline-entry--action' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="listitem"
              >
                {/* Left column: dot + line */}
                <div className="timeline-entry-spine" aria-hidden="true">
                  <div className="timeline-entry-dot" />
                  <div className="timeline-entry-line" />
                </div>

                {/* Right column: content */}
                <div className="timeline-entry-body">
                  {isToday && (
                    <div className="timeline-today-chip">Today</div>
                  )}

                  <div className="timeline-entry-top">
                    <time
                      className="timeline-entry-date"
                      dateTime={entry.date}
                    >
                      {entry.dateLabel
                        ? entry.dateLabel
                        : formatDate(entry.date, 'medium')}
                      {entry.time && (
                        <span className="timeline-entry-time"> · {entry.time}</span>
                      )}
                    </time>

                    <div className="timeline-entry-pills">
                      <Pill
                        variant={CATEGORY_VARIANT[entry.category] || 'default'}
                        size="sm"
                      >
                        {CATEGORY_LABELS[entry.category] || entry.category}
                      </Pill>
                      {entry.actionRequired && (
                        <Pill variant="required" size="sm">
                          Action required
                        </Pill>
                      )}
                    </div>
                  </div>

                  <div className="timeline-entry-title-row">
                    <h2 className="timeline-entry-title">{entry.title}</h2>
                    <InfoIcon source={entry.source} />
                  </div>

                  <p className="timeline-entry-description">{entry.description}</p>

                  {entry.note && (
                    <p className="timeline-entry-note">{entry.note}</p>
                  )}

                  {entry.actionText && (
                    <div className="timeline-entry-action-row">
                      {entry.actionUrl ? (
                        <a
                          href={entry.actionUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="timeline-action-link"
                        >
                          {entry.actionText} ↗
                        </a>
                      ) : (
                        <span className="timeline-action-text">{entry.actionText}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── TBA entries ── */}
      {tba.length > 0 && (
        <section className="timeline-tba-section" aria-labelledby="timeline-tba-heading">
          <h2 className="timeline-tba-heading" id="timeline-tba-heading">
            Dates Not Yet Published
          </h2>
          <p className="timeline-tba-subtitle">
            These events are real but the specific dates haven't been posted yet.
            Check the{' '}
            <a
              href="https://crms.leanderisd.org/calendar"
              target="_blank"
              rel="noopener noreferrer"
            >
              CRMS campus calendar ↗
            </a>{' '}
            each August when the school year kicks off.
          </p>

          <ul className="timeline-tba-list">
            {tba.map(entry => (
              <li key={entry.id} className="timeline-tba-entry">
                <div className="timeline-tba-top">
                  <span className="timeline-tba-label">
                    {entry.dateLabel || 'TBA — check campus calendar in August'}
                  </span>
                  <div className="timeline-tba-pills">
                    <Pill
                      variant={CATEGORY_VARIANT[entry.category] || 'default'}
                      size="sm"
                    >
                      {CATEGORY_LABELS[entry.category] || entry.category}
                    </Pill>
                    {entry.actionRequired && (
                      <Pill variant="required" size="sm">
                        Action required
                      </Pill>
                    )}
                  </div>
                </div>

                <div className="timeline-tba-title-row">
                  <h3 className="timeline-tba-title">{entry.title}</h3>
                  <InfoIcon source={entry.source} />
                </div>

                <p className="timeline-tba-description">{entry.description}</p>

                {entry.actionText && (
                  <div className="timeline-entry-action-row">
                    {entry.actionUrl ? (
                      <a
                        href={entry.actionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="timeline-action-link"
                      >
                        {entry.actionText} ↗
                      </a>
                    ) : (
                      <span className="timeline-action-text">{entry.actionText}</span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
